import { useQuery } from "@tanstack/react-query";
import { Share2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../../lib/http";
import {
  CONNECTOR_WORDS, RECORD_KIND_WORDS, entityParts, entityText, graphSections, predicateText, useNeioStatus,
  type GraphRecord, type TagGraph,
} from "../../lib/neio";
import { documentOpenPath } from "../../pages/documentViewFlow";
import { utcTime } from "../admin/format";
import { Empty } from "../States";
import { TableScroll } from "../TableScroll";
import { neioState } from "./TraceView";

function Linked({ title, items, none }: { title: string; items: { predicate: string; entity: string }[]; none: string }) {
  return (
    <section>
      <h4 className="graph-h">{title}</h4>
      {items.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>{none}</p> : (
        <ul className="stub-list">
          {items.map((l, i) => (
            <li key={`${l.entity}-${i}`}>
              <span className="muted">{predicateText(l.predicate)}</span> {entityText(l.entity)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function attributesText(r: GraphRecord): string {
  return Object.entries(r.attributes ?? {}).map(([k, v]) => `${k.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()} ${String(v)}`).join(", ");
}

function Sources({ records }: { records: GraphRecord[] }) {
  if (records.length === 0) return <p className="muted" style={{ fontSize: 13 }}>No source has recorded this tag yet.</p>;
  return (
    <TableScroll label="Sources that named this tag" className="card" style={{ padding: 0 }}>
      <table>
        <thead><tr><th>Source</th><th>What</th><th>Where</th><th>When</th><th>By</th></tr></thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={r.record ?? i}>
              <td>{(r.connector && CONNECTOR_WORDS[r.connector]) ?? r.connector ?? "Unknown source"}</td>
              <td>{[r.kind ? (RECORD_KIND_WORDS[r.kind] ?? r.kind) : null, r.label].filter(Boolean).join(", ")}{attributesText(r) && <div className="muted" style={{ fontSize: 12 }}>{attributesText(r)}</div>}</td>
              <td className="wrap-anywhere mono-sm">{r.locator ?? ""}</td>
              <td className="num nowrap">{utcTime(r.started_at)}</td>
              <td className="wrap-anywhere">{r.attributed_to ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

/** A tag's neighborhood as lists, without the fetching. */
export function TagGraphBody({ graph }: { graph: TagGraph }) {
  if (!graph.configured) {
    return (
      <Empty title="The graph store is not configured">
        {graph.detail ?? "This environment has no knowledge graph store, so there is nothing to show yet."}
      </Empty>
    );
  }
  const s = graphSections(graph);
  const empty = s.facts.length + s.identity.length + s.findings.length + s.drawings.length + s.other.length
    + s.documents.length + s.provenance.length === 0;
  if (empty) return <Empty title="The graph holds nothing about this tag yet">It is added as documents are swept and runs finish.</Empty>;
  return (
    <div className="stack">
      {graph.truncated && <p className="muted" role="note" style={{ fontSize: 13 }}>Only the first part of a long list is shown.</p>}
      <div className="grid-2">
        <section>
          <h4 className="graph-h">What it is</h4>
          {s.facts.length + s.identity.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>No manufacturer or model recorded.</p> : (
            <ul className="stub-list">
              {s.identity.map((l, i) => <li key={`i-${i}`}><span className="muted">{predicateText(l.predicate)}</span> {entityText(l.entity)}</li>)}
              {s.facts.map((f, i) => <li key={`f-${i}`}><span className="muted">{predicateText(f.predicate)}</span> {f.value}</li>)}
            </ul>
          )}
        </section>
        <section>
          <h4 className="graph-h">Documents and pages that mention it</h4>
          {s.documents.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>No document mentions it yet.</p> : (
            <ul className="stub-list">
              {s.documents.map((d, i) => {
                const page = entityParts(d.via ?? null);
                return (
                  <li key={`${d.document}-${i}`}>
                    {(() => {
                      const doc = entityParts(d.document);
                      const to = doc?.kind === "document"
                        ? documentOpenPath({ key: doc.parts[1], pkg: doc.parts[0], page: page?.kind === "page" ? Number(page.parts[2]) : null })
                        : null;
                      return to
                        ? <Link className="mono-sm" title="Open in the document view" to={to}>{entityText(d.document)}</Link>
                        : <span className="mono-sm" title={d.document}>{entityText(d.document)}</span>;
                    })()}
                    {page?.kind === "page" && <span className="muted">, page {page.parts[2]}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <Linked title="Findings" items={s.findings} none="No obsolescence finding recorded." />
        <Linked title="Drawings" items={s.drawings} none="Not on any P&ID drawing in the graph." />
      </div>
      {s.other.length > 0 && <Linked title="Also linked" items={s.other} none="" />}
      <section>
        <h4 className="graph-h">Sources that named it</h4>
        <Sources records={s.provenance} />
      </section>
    </div>
  );
}

/**
 * "Knowledge graph" on the register entry. Folded until opened, and only then read. Nothing at
 * all while NeIO's status is unknown or off.
 */
export function TagGraphSection({ pkg, tag }: { pkg: string; tag: string }) {
  const { enabled } = useNeioStatus();
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ["neio", "graph", pkg, tag],
    queryFn: () => apiGet("/api/v1/neio/graph/tags/{package}/{tag}", { path: { package: pkg, tag } }),
    enabled: enabled && open,
    retry: false,
  });
  if (!enabled) return null;
  const state = open ? neioState({
    what: "Reading the knowledge graph", loading: query.isPending, status: undefined, error: query.error,
    onRetry: () => void query.refetch(),
    notFound: <Empty title="NeIO Core does not know this tag yet" />,
  }) : null;
  return (
    <details className="card" style={{ marginBottom: 24 }} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary style={{ cursor: "pointer" }}>
        <span className="row" style={{ display: "inline-flex" }}><Share2 size={16} aria-hidden="true" /> Knowledge graph</span>
      </summary>
      <div style={{ marginTop: 12 }}>
        {state ?? (query.data && <TagGraphBody graph={query.data} />)}
      </div>
    </details>
  );
}
